import type { Cube } from '@cubejs-client/core'
import { createContext, useContext, type ReactNode } from 'react'

import { ChartWidgetView } from '@/features/report/widgets/ChartWidget'
import { KpiWidgetView } from '@/features/report/widgets/KpiWidget'
import { ListWidgetView } from '@/features/report/widgets/ListWidget'
import {
  chartFromRows,
  kpiFromRows,
  listFromRows,
  tableFromRows,
} from '@/features/report/widgets/results'
import { TableWidgetView } from '@/features/report/widgets/TableWidget'
import { viewFields, type ReportField } from '@/lib/cube-report'
import { messages } from '@/lib/messages'
import { periodLabel } from '@/lib/period'
import {
  documentFilterDefs,
  pageFilterDefs,
  type DocumentFilter,
  type DocumentQuery,
} from '@/services/cube/document'
import type { ReportRow } from '@/services/cube/report'

import { namedDataKey, previousDataKey, totalsDataKey, widgetDataKey } from '../runs/data-keys'
import { selectedText } from '../runs/filter-text'
import {
  DocumentDataSourceProvider,
  type DocumentDataSource,
  type NamedResult,
  type WidgetNodeName,
} from './data-source'
import { buildDocumentWidget } from './widget-model'
import { WidgetNotice } from './WidgetNotice'

/*
 * DONMUŞ veri kaynağı (dönem kopyası): `DocumentDataSource`'un ikinci uygulaması. Satırlar
 * `document_runs.data`'dan okunur, Cube'a sorgu atılmaz; düğümler kaynağın canlı mı donmuş mu
 * olduğunu bilmez. Cube meta'sı yalnız alan etiket/biçimi içindir (veri değil).
 */

interface FrozenConfig {
  views: ReadonlyMap<string, Cube>
  queries: Record<string, DocumentQuery>
  filters: DocumentFilter[]
  /** Üretimde seçilen filtreler: üye kısa adı -> değer. */
  filterValues: Record<string, unknown>
  /** `YYYY-MM` */
  period: string
  /** Sorgu anahtarı -> satırlar (`document_runs.data`). */
  data: Record<string, unknown>
  /** Baskı sayfası: widget'lar `printMode`'da çizilir. */
  printMode?: boolean
}

const FrozenConfigContext = createContext<FrozenConfig | null>(null)

function useFrozenConfig(): FrozenConfig {
  const config = useContext(FrozenConfigContext)
  if (!config) throw new Error('FrozenDocumentSource içinde değil')
  return config
}

const NO_FIELDS: ReadonlyMap<string, ReportField> = new Map()

/** Anahtarın satırları; yoksa tanımsız (kopyada o sorgunun verisi saklanmamış). */
const rowsOf = (data: FrozenConfig['data'], key: string) =>
  Array.isArray(data[key]) ? (data[key] as ReportRow[]) : undefined

function frozenNamedQuery(config: FrozenConfig, name: string | undefined): NamedResult | undefined {
  const def = name === undefined ? undefined : config.queries[name]
  if (!def) return undefined
  const cube = config.views.get(def.view)
  if (!cube)
    return { view: def.view, fields: NO_FIELDS, rows: undefined, state: 'forbidden', status: {} }

  const fields = viewFields(cube).fields
  const key = namedDataKey(name as string)
  const rows = rowsOf(config.data, key)
  if (!rows) {
    const error = new Error(messages.documents.runs.dataMissing(key))
    return {
      view: def.view,
      fields,
      rows: undefined,
      state: 'error',
      status: { isError: true, error },
    }
  }
  return {
    view: def.view,
    fields,
    rows,
    previousRows: rowsOf(config.data, previousDataKey(key)),
    state: 'ready',
    status: {},
  }
}

function FrozenWidget({ type, attrs }: { type: WidgetNodeName; attrs: Record<string, unknown> }) {
  const { views, filters, period, data, printMode } = useFrozenConfig()
  const built = buildDocumentWidget(
    type,
    attrs,
    views,
    pageFilterDefs(documentFilterDefs(filters)),
    period,
  )
  if (!built.ok) {
    return (
      <WidgetNotice
        kind={built.reason}
        message={built.reason === 'invalid' ? built.message : undefined}
      />
    )
  }
  const key = widgetDataKey(type, attrs)
  const rows = rowsOf(data, key)
  if (!rows) {
    return <WidgetNotice kind="invalid" message={messages.documents.runs.dataMissing(key)} />
  }
  const totals = rowsOf(data, totalsDataKey(key))
  const { widget } = built
  switch (widget.type) {
    case 'kpi':
      return (
        <KpiWidgetView
          widget={widget}
          data={kpiFromRows(widget, rows, rowsOf(data, previousDataKey(key)))}
          printMode={printMode}
        />
      )
    case 'table':
      return (
        <TableWidgetView widget={widget} data={tableFromRows(rows, totals)} printMode={printMode} />
      )
    case 'list':
      return (
        <ListWidgetView widget={widget} data={listFromRows(rows, totals)} printMode={printMode} />
      )
    case 'chart':
      return <ChartWidgetView widget={widget} data={chartFromRows(rows)} printMode={printMode} />
  }
}

export function FrozenDocumentSource({
  config,
  children,
}: {
  config: FrozenConfig
  children: ReactNode
}) {
  const source: DocumentDataSource = {
    periodLabel: periodLabel(config.period),
    filterText: (member) => selectedText(config, member),
    useNamedQuery: (name) => frozenNamedQuery(config, name),
    Widget: FrozenWidget,
  }
  return (
    <FrozenConfigContext.Provider value={config}>
      <DocumentDataSourceProvider value={source}>{children}</DocumentDataSourceProvider>
    </FrozenConfigContext.Provider>
  )
}

export type { FrozenConfig }
